import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { mostraAlertaError } from "../../../componentes/alerts/sweetAlert";
import { AxiosPrivado } from "../../../componentes/axios/Axios";
import { ErroresPeticion } from "../../../componentes/axios/ErroresPeticion";
import Card from "../../../componentes/contenedores/Card";
import { IdentificarEstacion } from "../../../componentes/estacion/Estacion";
import Dropzone from "../../../componentes/imagenes/Dropzone";
import ModalBootstrap from "../../../componentes/modal/ModalBootstrap";
import Page from "../../../componentes/plantilla/Page";
import Tablas from "../../../componentes/tablas/TablasClientes";
import { CajaVerificar, ImagenProducto, ProveedoresListar } from "../../../configuracion/apiUrls";
import { useContextEmpleado } from "../../../contexto/empleado/EmpleadoContext";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";
import ModalLoteProducto from "./ModalLoteProducto";
import ModalPago from "./ModalPago";
import { formatoMoneda } from "../../../configuracion/validaciones";

const HomeCompras = () => {
    const { usuario } = useContextUsuario();
    const fechaActual = moment().format("dddd, DD [de] MMMM [de] YYYY");
    const [notaGeneral, setNotaGeneral] = useState("");
    const { listaProductos, listaProveedores, ActualizarLista, setListaProveedores } = useContextEmpleado();
    const [productoSearchTerm, setProductoSearchTerm] = useState("");
    const [productos, setProductos] = useState([]);
    const [proveedorSearchTerm, setProveedorSearchTerm] = useState("");
    const [proveedores, setProveedores] = useState([]);
    const [modalIsOpenProveedor, setModalIsOpenProveedor] = useState(false);
    const [modalIsOpenProducto, setModalIsOpenProducto] = useState(false);
    const [productosSeleccionados, setProductosSeleccionados] = useState([]);
    const [selectedProveedor, setSelectedProveedor] = useState({});
    const [dropzoneKey, setDropzoneKey] = useState(Date.now());
    const [nuevasImagenes, setNuevasImagenes] = useState([]);
    const [cajaId, setCajaId] = useState(null);

    const verificarYCargarCaja = async () => {
        try {
            const estacionDatos = await IdentificarEstacion();
            const response = await AxiosPrivado.post(CajaVerificar, { estacionDatos });
            if (response.data.estado === "CE") {
                mostraAlertaError("La caja ya está cerrada. No se puede registrar compras.");
                return;
            }
            const caja = response.data;
            setCajaId(caja.id);
            return true;
        } catch (error) {
            ErroresPeticion(error);
            return false;
        }
    };

    useEffect(() => {
        verificarYCargarCaja();
        if (!Array.isArray(listaProveedores) || listaProveedores.length === 0) {
            ActualizarLista(ProveedoresListar, setListaProveedores);
        }
    }, []);

    useEffect(() => {
        if (!Array.isArray(listaProveedores) || listaProveedores.length === 0) {
            setProveedores([]);
            return;
        }

        const transformados = listaProveedores.map((p) => ({
            id: p.id,
            nombre: p.nombre,
            telefono: p.telefono,
            correo: p.correo,
        }));

        setProveedores(transformados);
    }, [listaProveedores]);
    useEffect(() => {
        if (!Array.isArray(listaProductos) || listaProductos.length === 0) {
            setProductos([]);
            return;
        }
        const transformados = listaProductos.map((p) => {
            const imgs = p.productoImagens && p.productoImagens.length > 0
                ? p.productoImagens.map((img) => ImagenProducto + img.imagen)
                : ["/productos.jpg"];
            return {
                id: p.id,
                nombre: p.nombre,
                codigo: p.codigo,
                existencia: p.existencia,
                precio: p.precio,
                costo: p.costo,
                descripcion: p.descripcion,
                imagenes: imgs,
                cantidad: 1,
                lote: p.lote || null,
            };
        });
        setProductos(transformados);
    }, [listaProductos]);

    const cargarProveedores = () => {
        if (!Array.isArray(listaProveedores) || listaProveedores.length === 0) {
            ActualizarLista(ProveedoresListar, setListaProveedores);
            return;
        }
        const transformados = listaProveedores.map((p) => ({
            id: p.id,
            nombre: p.nombre,
            telefono: p.telefono,
            correo: p.correo,
        }));
        setProveedores(transformados);
    };

    const filteredProveedores = proveedores.filter((proveedor) =>
        proveedor.nombre
            .toLowerCase()
            .includes(proveedorSearchTerm.toLowerCase())
    );

    const filteredProductos = productos.filter((producto) =>
        (producto.nombre + producto.codigo)
            .toLowerCase()
            .includes(productoSearchTerm.toLowerCase())
    );

    const actualizarPrecioOCosto = (id, campo, valor) => {
        setProductosSeleccionados(prev =>
            prev.map(p => {
                if (p.id !== id) return p;
                const nuevoValor = parseFloat(valor) || 0;
                const precioAnterior = p.precio;
                const costoAnterior = p.costo;
                if (campo === 'precio') {
                    return {
                        ...p,
                        precio: nuevoValor,
                        precioAnterior,
                    };
                }
                if (campo === 'costo') {
                    const nuevoCosto = Math.round(nuevoValor);
                    return {
                        ...p,
                        costo: nuevoCosto,
                        costoAnterior,
                    };
                }
                return p;
            })
        );
    };

    const handleSaveLote = (productoActualizado) => {
        setProductosSeleccionados(prev =>
            prev.map(p => (p.id === productoActualizado.id ? productoActualizado : p))
        );
    };

    const columnasTabla = useMemo(() => [
        {
            title: "Opciones",
            data: null,
            orderable: false,
            createdCell: (td, cellData, rowData) => {
                const root = createRoot(td);
                root.render(
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "1em",
                    }}>
                        <button
                            className="btn btn-danger mx-1"
                            onClick={() =>
                                setProductosSeleccionados((prev) =>
                                    prev.filter((p) => p.id !== rowData.id)
                                )
                            }
                        >
                            <i className="fas fa-trash" />
                        </button>
                        <ModalLoteProducto
                            producto={rowData}
                            onSave={handleSaveLote}
                        />
                    </div>
                );
            },
        },
        {
            title: "Nombre",
            data: null,
            render: (data, type, rowData) => {
                return `
                <div>
                  <strong class="text-wrap">${rowData.nombre}</strong><br />
                  <small class="text-muted">Código: ${rowData.codigo || ""}</small>
                  <br />
                  <small class="text-muted">Costo: ${formatoMoneda(rowData.costo || "")}</small>
                  <small class="text-muted">Precio: ${formatoMoneda(rowData.precio || "")}</small>
                </div>`;
            },
        },
        {
            title: "Lote",
            data: null,
            render: (rowData) => { return rowData.lote?.nombre_lote ? rowData.lote.nombre_lote : 'N/D'; },
        },
        {
            title: "Cantidad",
            data: "cantidad"
        },
        {
            title: "Precio",
            data: null,
            render: (rowData) => {
                return `<span style="text-align: right; display: block;">${formatoMoneda(rowData.precio)}</span>`;
            },
        },
        {
            title: "Costo",
            data: null,
            render: (rowData) => {
                return `<span style="text-align: right; display: block;">${formatoMoneda(rowData.costo)}</span>`;
            },
        },
        {
            title: "Subtotal",
            data: null,
            render: (rowData) => {
                return `<span style="text-align: right; display: block;">${formatoMoneda(rowData.costo * (rowData.lote?.cantidad || rowData.cantidad || 0))}</span>`;
            },
        },
    ], [productosSeleccionados]);


    const agregarProducto = (producto) => {
        setProductosSeleccionados((prev) => {
            const existe = prev.find((p) => p.id === producto.id);
            if (existe) {
                return prev.map((p) =>
                    p.id === producto.id
                        ? {
                            ...p,
                            precioAnterior: p.precioAnterior ?? parseFloat(p.precio),
                            costoAnterior: p.costoAnterior ?? parseFloat(p.costo),
                        }
                        : p
                );
            } else {
                return [
                    ...prev,
                    {
                        ...producto,
                        cantidad: producto.lote?.cantidad || 1,
                        precio: parseFloat(producto.precio),
                        costo: parseFloat(producto.costo),
                        precioAnterior: parseFloat(producto.precio),
                        costoAnterior: parseFloat(producto.costo),
                        lote: producto.lote || null,
                    },
                ];
            }
        });
        setModalIsOpenProducto(false);
    };

    const handleProveedorSeleccionado = (proveedor) => {
        setSelectedProveedor((prev) => ({
            ...prev,
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
        }));
        setModalIsOpenProveedor(false);
    };

    const datos = {
        titulo: {
            titulo: "Nueva compra",
            url: "/app/admin/compras/nueva",
            tituloUrl: "Menu Compras",
            nombreUrl: "Inicio ",
        },
    };

    const calculartotal = useMemo(() => {
        return productosSeleccionados.reduce((total, item) => {
            const cantidadParaCalculo = item.lote?.cantidad || item.cantidad || 0;
            const costo = parseFloat(item.costo) || 0;
            return total + (costo * cantidadParaCalculo);
        }, 0).toFixed(2);
    }, [productosSeleccionados]);

    const buscarNumeroFactura = (lista) => {
        let n = 0;
        if (lista && lista.length > 0) {
            lista.forEach(f => {
                n = f.numero;
            });
        }
        return n;
    };

    const buscarNumeroRecibo = (lista) => {
        let n = 0;
        if (lista && lista.length > 0) {
            lista.forEach(f => {
                n = f.id;
            });
        }
        return n;
    };

    const [pago, setPago] = useState({
        documento: "",
        numero: [],
        facturas: [],
        recibos: [],
        proveedorId: "",
        empleadoId: "",
        fecha: new Date().toLocaleDateString(),
        items: [],
        total: "0.00",
        cambio: "0.00",
    });

    useEffect(() => {
        setPago(prev => ({
            ...prev,
            empleadoId: usuario.datoPersonales.id,
            proveedorId: selectedProveedor?.proveedorId || "",
            items: productosSeleccionados,
            total: calculartotal,
            cambio: prev.cambio || "0.00",
            fecha: new Date().toLocaleDateString(),
            numero:
                pago.documento === "FA"
                    ? buscarNumeroFactura(pago.facturas)
                    : buscarNumeroRecibo(pago.recibos),
        }));
    }, [productosSeleccionados, selectedProveedor, calculartotal, pago.documento, pago.facturas, pago.recibos, usuario.datoPersonales.id]);

    const resetCompra = () => {
        setPago({
            documento: "",
            numero: "",
            facturas: [],
            recibos: [],
            proveedorId: "",
            empleadoId: "",
            fecha: new Date().toLocaleDateString(),
            items: [],
            total: "0.00",
            cambio: "0.00",
        });
    };

    const limpiarTodo = () => {
        setProductosSeleccionados([]);
        setNotaGeneral("");
        setSelectedProveedor({});
        setNuevasImagenes([]);
        setDropzoneKey(Date.now());
        resetCompra();
    };
    // Pagina de productos
    const [paginaActual, setPaginaActual] = useState(1);
    const productosPorPagina = 5;
    const indexUltimo = paginaActual * productosPorPagina;
    const indexPrimero = indexUltimo - productosPorPagina;
    const productosPagina = filteredProductos.slice(indexPrimero, indexUltimo);
    const totalPaginas = Math.ceil(filteredProductos.length / productosPorPagina);
    // Pagina de proveedores
    const [paginaActualProveedor, setPaginaActualProveedor] = useState(1);
    const proveedoresPorPagina = 5;
    const indexUltimoProveedor = paginaActualProveedor * proveedoresPorPagina;
    const indexPrimeroProveedor = indexUltimoProveedor - proveedoresPorPagina;
    const proveedoresPagina = filteredProveedores.slice(indexPrimeroProveedor, indexUltimoProveedor);
    const totalPaginasProveedor = Math.ceil(filteredProveedores.length / proveedoresPorPagina);
    return (
        <Page datos={datos}>
            <Card
                pie={<>
                    <ModalPago
                        Total={parseFloat(pago.total || 0)}
                        pago={pago}
                        setPago={setPago}
                        factura={pago}
                        resetCompra={resetCompra}
                        notaGeneral={notaGeneral}
                        nuevasImagenes={nuevasImagenes}
                        limpiarTodo={limpiarTodo}
                        productosSeleccionados={productosSeleccionados}
                        proveedorId={selectedProveedor?.proveedorId}
                        cajaId={cajaId}
                    />
                </>}>
                <h3 className="text-muted mb-3 text-center text-md-start">{fechaActual}</h3>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                    <div className="border rounded p-3 shadow-sm bg-light">
                        <h6 className="text-muted mb-3">Acciones</h6>
                        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                            <ModalBootstrap
                                titulo="Agregar Productos"
                                nombreBoton="Buscar producto"
                                estiloBoton="btn btn-primary me-2 mb-2 mx-1"
                                iconBoton="fas fa-cart-plus"
                                modalIsOpen={modalIsOpenProducto}
                                setModalIsOpen={setModalIsOpenProducto}
                            >
                                <div className="mb-3 d-flex">
                                    <div className="col-md-6 col-sm-8">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Buscar producto por nombre o código..."
                                            value={productoSearchTerm}
                                            onChange={(e) => setProductoSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped table-hover shadow-sm">
                                        <thead className="bg-dark text-white">
                                            <tr>
                                                <th>Imagen</th>
                                                <th>Nombre</th>
                                                <th>Codigo</th>
                                                <th>Precio</th>
                                                <th>Agregar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productosPagina.length > 0 ? (
                                                productosPagina.map((producto) => (
                                                    <tr key={producto.id}>
                                                        <td>
                                                            <img
                                                                src={producto.imagenes[0]}
                                                                alt={producto.nombre}
                                                                className="img-thumbnail"
                                                                style={{ width: "100px" }}
                                                            />
                                                        </td>
                                                        <td>{producto.nombre}</td>
                                                        <td>L{producto.codigo}</td>
                                                        <td>L{producto.precio}</td>
                                                        <td className="text-center align-middle">
                                                            <button
                                                                className="btn btn-success btn-sm"
                                                                onClick={() => agregarProducto(producto)}
                                                            >
                                                                <i className="fa-solid fa-cart-plus" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted">
                                                        No se encontraron productos.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <nav className="mt-2">
                                        <ul className="pagination pagination-sm justify-content-center shadow-sm bg-light p-3 rounded">
                                            <li className={`page-item ${paginaActual === 1 ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setPaginaActual(paginaActual - 1)}
                                                    aria-label="Anterior"
                                                >
                                                    <i className="fas fa-chevron-left me-1"></i> Anterior
                                                </button>
                                            </li>
                                            {(() => {
                                                const pagesToShow = 5;
                                                const halfPages = Math.floor(pagesToShow / 2);
                                                let startPage = Math.max(1, paginaActual - halfPages);
                                                let endPage = Math.min(totalPaginas, paginaActual + halfPages);

                                                if (paginaActual <= halfPages) {
                                                    endPage = Math.min(totalPaginas, pagesToShow);
                                                }
                                                if (paginaActual > totalPaginas - halfPages) {
                                                    startPage = Math.max(1, totalPaginas - pagesToShow + 1);
                                                }

                                                const pageButtons = [];
                                                if (startPage > 1) {
                                                    pageButtons.push(
                                                        <li key={1} className={`page-item ${paginaActual === 1 ? "active" : ""}`}>
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActual(1)}>1</button>
                                                        </li>
                                                    );
                                                    if (startPage > 2) {
                                                        pageButtons.push(
                                                            <li key="ellipsis-start" className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                }

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pageButtons.push(
                                                        <li
                                                            key={i}
                                                            className={`page-item ${paginaActual === i ? "active" : ""}`}
                                                        >
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActual(i)}>
                                                                {i}
                                                            </button>
                                                        </li>
                                                    );
                                                }

                                                if (endPage < totalPaginas) {
                                                    if (endPage < totalPaginas - 1) {
                                                        pageButtons.push(
                                                            <li key="ellipsis-end" className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    pageButtons.push(
                                                        <li key={totalPaginas} className={`page-item ${paginaActual === totalPaginas ? "active" : ""}`}>
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActual(totalPaginas)}>{totalPaginas}</button>
                                                        </li>
                                                    );
                                                }

                                                return pageButtons;
                                            })()}
                                            <li className={`page-item ${paginaActual === totalPaginas ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setPaginaActual(paginaActual + 1)}
                                                    aria-label="Siguiente"
                                                >
                                                    Siguiente <i className="fas fa-chevron-right ms-1"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </ModalBootstrap>
                            <ModalBootstrap
                                titulo="Seleccionar Proveedor"
                                nombreBoton="Buscar proveedor"
                                estiloBoton="btn btn-primary me-2 mb-2 mx-1"
                                iconBoton="fa-solid fa-magnifying-glass"
                                modalIsOpen={modalIsOpenProveedor}
                                setModalIsOpen={setModalIsOpenProveedor}
                            >
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar cliente..."
                                        value={proveedorSearchTerm}
                                        onChange={(e) => setProveedorSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped table-hover shadow-smr">
                                        <thead className="bg-dark text-white">
                                            <tr>
                                                <th>ID</th>
                                                <th>Nombre</th>
                                                <th>Agregar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {proveedoresPagina.length > 0 ? (
                                                proveedoresPagina.map((proveedor) => (
                                                    <tr key={proveedor.id}>
                                                        <td>{proveedor.id}</td>
                                                        <td>{proveedor.nombre}</td>
                                                        <td className="text-center align-middle">
                                                            <button
                                                                className="btn btn-success btn-sm"
                                                                onClick={() =>
                                                                    handleProveedorSeleccionado(proveedor)
                                                                }
                                                            >
                                                                <i className="fa-regular fa-square-check" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="text-center text-muted">
                                                        No se encontraron proveedores.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <nav className="mt-2">
                                        <ul className="pagination pagination-sm justify-content-center shadow-sm bg-light p-3 rounded">
                                            <li className={`page-item ${paginaActualProveedor === 1 ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setPaginaActualProveedor(paginaActualProveedor - 1)}
                                                    aria-label="Anterior"
                                                >
                                                    <i className="fas fa-chevron-left me-1"></i> Anterior
                                                </button>
                                            </li>
                                            {(() => {
                                                const pagesToShow = 5;
                                                const halfPages = Math.floor(pagesToShow / 2);
                                                let startPage = Math.max(1, paginaActualProveedor - halfPages);
                                                let endPage = Math.min(totalPaginasProveedor, paginaActualProveedor + halfPages);

                                                if (paginaActualProveedor <= halfPages) {
                                                    endPage = Math.min(totalPaginasProveedor, pagesToShow);
                                                }
                                                if (paginaActualProveedor > totalPaginasProveedor - halfPages) {
                                                    startPage = Math.max(1, totalPaginasProveedor - pagesToShow + 1);
                                                }

                                                const pageButtons = [];
                                                if (startPage > 1) {
                                                    pageButtons.push(
                                                        <li key={1} className={`page-item ${paginaActualProveedor === 1 ? "active" : ""}`}>
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActualProveedor(1)}>1</button>
                                                        </li>
                                                    );
                                                    if (startPage > 2) {
                                                        pageButtons.push(
                                                            <li key="ellipsis-prov-start" className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                }

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pageButtons.push(
                                                        <li
                                                            key={i}
                                                            className={`page-item ${paginaActualProveedor === i ? "active" : ""}`}
                                                        >
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActualProveedor(i)}>
                                                                {i}
                                                            </button>
                                                        </li>
                                                    );
                                                }

                                                if (endPage < totalPaginasProveedor) {
                                                    if (endPage < totalPaginasProveedor - 1) {
                                                        pageButtons.push(
                                                            <li key="ellipsis-prov-end" className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    pageButtons.push(
                                                        <li key={totalPaginasProveedor} className={`page-item ${paginaActualProveedor === totalPaginasProveedor ? "active" : ""}`}>
                                                            <button className="page-link fw-bold" onClick={() => setPaginaActualProveedor(totalPaginasProveedor)}>{totalPaginasProveedor}</button>
                                                        </li>
                                                    );
                                                }

                                                return pageButtons;
                                            })()}
                                            <li className={`page-item ${paginaActualProveedor === totalPaginasProveedor ? "disabled" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setPaginaActualProveedor(paginaActualProveedor + 1)}
                                                    aria-label="Siguiente"
                                                >
                                                    Siguiente <i className="fas fa-chevron-right ms-1"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </ModalBootstrap>
                        </div>
                    </div>
                </div>
                <div className="mb-4">
                    {selectedProveedor && selectedProveedor.proveedorNombre ? (
                        <div className="alert alert-info">
                            <strong>Proveedor seleccionado:</strong>{" "}
                            {selectedProveedor.proveedorNombre}
                        </div>
                    ) : (
                        <h6 className="text-muted">No se ha seleccionado proveedor</h6>
                    )}
                </div>
                <div className="mb-3">
                    {productosSeleccionados.length > 0 ? (
                        <Tablas datos={productosSeleccionados} columnas={columnasTabla} id="tablaSeleccionados" />
                    ) : (
                        <h6>No hay productos seleccionados</h6>
                    )}
                </div>
                <h6 className="fw-bold mt-4">
                    <i className="fas fa-sticky-note me-1"></i> Nota del la compra:
                </h6>
                <hr />
                <div className="mb-3">
                    <textarea
                        id="notaGeneral"
                        className="form-control border-primary rounded shadow-sm"
                        placeholder="Agrega una nota general..."
                        value={notaGeneral}
                        onChange={(e) => setNotaGeneral(e.target.value)}
                    ></textarea>
                </div>
                <h6 className="fw-bold mt-4">
                    <i className="fas fa-file-invoice me-1"></i> Imagen de factura de compra:
                </h6>
                <hr></hr>
                <div className="row mb-3">
                    <div className="col-12">
                        <Dropzone
                            key={dropzoneKey}
                            max={1}
                            files={nuevasImagenes}
                            setFiles={setNuevasImagenes}
                        />
                    </div>
                </div>
            </Card>

        </Page>
    );
}

export default HomeCompras;